import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-lg text-muted-foreground mb-2">Transparency, Security, and Trust</p>
        <p className="text-sm text-muted-foreground mb-8">Effective Date: Jan 01, 2025 | Last Updated: Aug 23, 2025</p>

        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              HotelAnalytics is a product of Prowen Technologies India Private Limited ("ProwenTech", "we", "our", "us"). We are committed to protecting your personal information. This Privacy Policy describes how we collect, use, and safeguard your data, including transactions processed through our payment partner Razorpay.
            </p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>
            <ul className="space-y-3 text-muted-foreground list-disc pl-6">
              <li><strong className="text-foreground">Account Data:</strong> Name, email, phone, business details.</li>
              <li><strong className="text-foreground">Payment Data:</strong> Billing details, transaction IDs, mode of payment (processed securely via Razorpay).</li>
              <li><strong className="text-foreground">Usage Data:</strong> Browser type, IP, device identifiers, cookies, analytics.</li>
              <li><strong className="text-foreground">Support Data:</strong> Messages, inquiries, and support interactions.</li>
            </ul>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">3. How We Use Your Information</h2>
            <ul className="space-y-2 text-muted-foreground list-disc pl-6">
              <li>To provide and improve HotelAnalytics services.</li>
              <li>To process payments via Razorpay.</li>
              <li>To comply with legal and regulatory obligations.</li>
              <li>To communicate important updates, promotions, and security alerts.</li>
            </ul>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Sharing of Data</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">We may share your data with:</p>
            <ul className="space-y-2 text-muted-foreground list-disc pl-6">
              <li>Razorpay (for payment processing).</li>
              <li>Authorized third-party service providers (analytics, cloud hosting).</li>
              <li>Regulators or authorities when required by law.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3 font-medium">We do not sell your personal data.</p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use industry-standard security practices, and Razorpay is PCI-DSS compliant. While we take precautions, no system is 100% secure.
            </p>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Your Rights</h2>
            <ul className="space-y-2 text-muted-foreground list-disc pl-6">
              <li>Request access, correction, or deletion of your data.</li>
              <li>Opt out of marketing communications.</li>
              <li>Contact us for grievances or data-related queries.</li>
            </ul>
          </section>

          <Separator />

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Customer Support</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              For assistance with accessing your purchased leads or credit-related queries, reach out to:
            </p>
            <div className="p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground space-y-1">
              <p>Email: <a href="mailto:support@prowentech.com" className="text-primary hover:underline">support@prowentech.com</a></p>
              <p>Phone: +91-9087804440</p>
              <p>Website: <a href="https://www.prowentech.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.prowentech.com</a></p>
              <div className="pt-2">
                <p className="font-medium text-foreground">Address:</p>
                <p>Prowen Technologies India Private Limited,</p>
                <p>14/3, LGB Nagar,</p>
                <p>Saravanampatti Post,</p>
                <p>Coimbatore,</p>
                <p>Tamil Nadu 641035</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
